import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { FaqList } from "@/components/site/FaqList";
import { TextField, SelectField, AreaField, readForm } from "@/components/site/FormFields";
import { breadcrumbScript } from "@/lib/schema";
import { submitInquiry } from "@/lib/submit-inquiry";

const URL = "https://avoluscars.com/partners";
const TITLE = "Partner Program | Weddings, Hotels & Photographers | Avolus Cars";
const DESC =
  "Refer luxury car rentals in Vancouver & Richmond and earn a finder's fee. For wedding planners, photographers, hotels & concierges.";

const partnerFaqs = [
  {
    q: "How is the finder's fee paid?",
    a: "Approved partners earn a competitive finder's fee on every completed booking that comes through their referral code. Payment details are shared on approval.",
  },
  {
    q: "When is the finder's fee paid?",
    a: "The fee is earned once a referred rental is completed. Timing and payment details are shared on approval, so you know exactly what to expect before your first referral.",
  },
  {
    q: "Is there a cost to join?",
    a: "No. Applying to the Avolus partner program is free. Submit the form below and we'll be in touch to talk through fit and next steps.",
  },
  {
    q: "Can my clients book directly?",
    a: "Yes. Your clients can book online or call us directly and simply mention your referral code. We handle the booking, delivery and handover from there.",
  },
  {
    q: "Which areas do you serve?",
    a: "We deliver throughout Greater Vancouver, with complimentary delivery within Vancouver and to YVR airport. Richmond, Burnaby, North and West Vancouver and Whistler are available by arrangement.",
  },
];

const partnerTypes = [
  "Wedding planner",
  "Venue",
  "Photographer or videographer",
  "Hotel or concierge",
  "Travel agency or DMC",
  "Event or corporate planner",
  "Other",
];

export const Route = createFileRoute("/partners")({
  component: PartnersPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: partnerFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Partner Program", url: URL },
      ]),
    ],
  }),
});

const audiences = [
  {
    title: "Wedding planners & venues",
    body: "Couples ask you about the arrival long before they ask about anything else on the drive. The Mercedes-Benz S580 is the classic choice for the bride and immediate family, the Cadillac Escalade moves the bridal party with room for gowns and suits, and the Range Rover P530 LWB makes a calm, formal SUV entrance. We stage the car to your timeline so your day runs the way you planned it.",
  },
  {
    title: "Photographers & videographers",
    body: "A well-placed car changes a frame. For styled shoots, engagement sessions and brand content, we bring the vehicle clean and on time, and we're used to working around light, locations and shot lists. Recommend us to clients who want the car in the story, and keep the shoot moving.",
  },
  {
    title: "Hotels & concierges",
    body: "When a guest asks for something better than a standard rental, you can say yes. We deliver to the lobby in Vancouver and Richmond, walk the guest through the car, and collect it when they're done. No counter, no shuttle, no paperwork headaches for your front desk.",
  },
  {
    title: "Travel agencies & DMCs",
    body: "Visiting clients often want one reliable vehicle for the whole trip. We handle multi-day rentals with delivery to YVR or the hotel, so your itinerary starts the moment they land and the car is simply there when they need it.",
  },
  {
    title: "Event & corporate planners",
    body: "Product launches, executive visits, offsites and gala nights all need vehicles that match the occasion. Tell us the dates and the guest list, and we'll suggest the right mix from the fleet and deliver to where the event is happening.",
  },
];

const steps = [
  { n: "01", title: "Apply", body: "Fill in the short form below with your business details and the kind of clients you work with." },
  { n: "02", title: "Receive your referral code", body: "Once approved, you get a personal referral code to share with clients by link, email or word of mouth." },
  { n: "03", title: "Earn on every completed booking", body: "Whenever a client books with your code and the rental is completed, you earn a finder's fee." },
];

const fleetNames = [
  "Cadillac Escalade",
  "BMW X7 M60i",
  "Mercedes-Benz S580",
  "Porsche Macan",
  "Range Rover P530 LWB",
  "BMW M440i",
  "BMW X1",
  "BMW M3 Competition",
];

function PartnersPage() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const get = readForm(e.currentTarget);
    const business = get("business");
    const name = get("contactName");
    const email = get("email");
    const phone = get("phone");
    const type = get("partnerType");
    if (!business || !name || !email || !phone || !type)
      return toast.error("Please fill in all required fields.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email.");

    const message = [
      "PARTNER APPLICATION",
      `Business name: ${business}`,
      `Contact name: ${name}`,
      `Partner type: ${type}`,
      `Website or Instagram: ${get("website") || "Not provided"}`,
      `Estimated referrals per month: ${get("referrals") || "Not provided"}`,
      `Booking in mind: ${get("immediate") || "Not provided"}`,
      "",
      `Message: ${get("message") || "Not provided"}`,
    ]
      .join("\n")
      .slice(0, 2000);

    setSubmitting(true);
    const { error } = await submitInquiry({
      name: name.slice(0, 100),
      email,
      phone: phone.slice(0, 30),
      message,
      formType: "partner",
      subject: `New Partner Application: ${business}`.slice(0, 200),
    });
    setSubmitting(false);
    if (error) return toast.error("Something went wrong. Please try again or call us.");
    setDone(true);
  };

  return (
    <Layout inner>
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Avolus Partner Program</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground max-w-4xl leading-[1.05]">
              Partner With Avolus Cars
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Recommend Vancouver's delivery-first luxury car rental to your clients and earn a
              finder's fee on every completed booking. Your clients get a better car and a
              smoother day. You get a partner you can trust with your name.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#apply"
                className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 hover:tracking-[0.32em] hover:scale-[1.02] transition-all duration-300"
              >
                Apply to Partner
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Who We Partner With</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              Built for people whose clients expect more.
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-10">
              Our best bookings rarely come from a search box. They come from someone the client
              already trusts: the planner running their wedding, the concierge at their hotel,
              the photographer shooting their engagement. If that's you, we'd like to work
              together.
            </p>
            <div className="space-y-10">
              {audiences.map((a) => (
                <div key={a.title}>
                  <h3 className="font-display text-2xl text-foreground">{a.title}</h3>
                  <p className="mt-3 text-foreground/80 leading-relaxed">{a.body}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border bg-muted/40">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-12">
              Three steps, no complications.
            </h2>
            <ol className="grid gap-8 md:grid-cols-3">
              {steps.map((s) => (
                <li key={s.n} className="border border-border bg-card p-8">
                  <div className="text-xs tracking-[0.3em] text-muted-foreground">{s.n}</div>
                  <h3 className="mt-4 font-display text-2xl text-foreground">{s.title}</h3>
                  <p className="mt-3 text-foreground/80 leading-relaxed">{s.body}</p>
                </li>
              ))}
            </ol>
            <p className="mt-10 text-foreground/80 leading-relaxed max-w-3xl">
              You don't need to manage bookings, chase payments or answer questions about the
              cars. Point your client to us with your code, and our team takes care of the rest,
              from the first call to the moment the car is collected.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Why Avolus</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-8">
              A rental your clients will thank you for.
            </h2>
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Avolus is delivery-first. There's no counter and no shuttle: we bring the car to
                the hotel, home, venue or airport, walk your client through it, and collect it
                when they're finished. Delivery is complimentary within Vancouver and to YVR
                airport, and the rest of Greater Vancouver is available by arrangement. Learn
                more on our{" "}
                <Link to="/delivery" className="underline underline-offset-4 hover:text-foreground">
                  delivery page
                </Link>
                .
              </p>
              <p>
                Every car arrives detailed and fuelled, with no rental stickers or branding, so
                it looks the part in photos and on arrival. Our concierge is easy to reach, and
                we keep you in the loop when your client books.
              </p>
              <p>The fleet your clients can choose from:</p>
            </div>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2 text-foreground">
              {fleetNames.map((n) => (
                <li key={n} className="border border-border px-5 py-4 font-display text-lg">
                  {n}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-foreground/80 leading-relaxed">
              See every vehicle, photo and spec in{" "}
              <Link to="/fleet" className="underline underline-offset-4 hover:text-foreground">
                the fleet
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      <section id="apply" className="py-20 lg:py-28 border-t border-border scroll-mt-28">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Apply</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-4">
              Apply to partner.
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-10">
              Tell us a little about your business. We review every application personally and
              reply by email.
            </p>
          </Reveal>
          {done ? (
            <div role="status" className="border border-border p-12 text-center bg-card">
              <h3 className="font-display text-3xl text-foreground mb-3">Application received.</h3>
              <p className="text-foreground/70">
                Thank you. We'll review your details and be in touch by email shortly.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="grid gap-6 md:grid-cols-2 border border-border p-8 md:p-10 bg-card"
            >
              <TextField id="business" label="Business name" required maxLength={120} />
              <TextField id="contactName" label="Contact name" required maxLength={100} />
              <TextField id="email" label="Email" type="email" required maxLength={255} />
              <TextField id="phone" label="Phone" type="tel" required maxLength={30} />
              <SelectField id="partnerType" label="Partner type" options={partnerTypes} required />
              <SelectField
                id="referrals"
                label="Estimated referrals per month"
                options={["1-2", "3-5", "6+", "Not sure"]}
              />
              <TextField id="website" label="Website or Instagram" maxLength={200} />
              <SelectField
                id="immediate"
                label="Booking in mind?"
                options={["Yes, right now", "In the next 30 days", "Just exploring"]}
              />
              <AreaField id="message" label="Message" />
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Submit Application"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-border">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Partner FAQ</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-10">
              Partner questions, answered.
            </h2>
          </Reveal>
          <FaqList items={partnerFaqs} prefix="p" />
          <p className="mt-10 text-sm text-muted-foreground">
            Have another question?{" "}
            <Link to="/contact" className="text-foreground underline underline-offset-4">
              Talk to the concierge
            </Link>
            .
          </p>
        </div>
      </section>
    </Layout>
  );
}
